import { useState } from "react";
import { Check, X } from "lucide-react";
import type { AdminAreaSuggestionView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { Banner, Button, Modal, useToast } from "@/shared/rd";
import { useApproveAreaSuggestionMutation } from "../services/useApproveAreaSuggestionMutation";
import { useRejectAreaSuggestionMutation } from "../services/useRejectAreaSuggestionMutation";
import { AreaEditor } from "./AreaEditor";

type Props = {
  suggestion: AdminAreaSuggestionView;
};

// Approve/reject actions for a single pending suggestion. Approve opens the
// area drawer pre-filled from the raw name (governorate fixed); the drawer
// calls the approve endpoint via `onApprove` below, so a human can correct
// spelling before it becomes canonical reference data. Reject asks for
// confirmation (the API can't undo a rejection), then calls the reject
// endpoint directly. Successes are shell toasts; failures stay where they
// happened (the drawer, the modal) as a Banner.
export function AreaSuggestionActions({ suggestion }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const approveMutation = useApproveAreaSuggestionMutation();
  const rejectMutation = useRejectAreaSuggestionMutation();
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  /** The failed reject's explanation ("" when the server said nothing). */
  const [rejectError, setRejectError] = useState<string | null>(null);

  const governorateName =
    suggestion.governorateNameAr && suggestion.governorateNameAr.length > 0
      ? suggestion.governorateNameAr
      : (suggestion.governorateNameEn ?? suggestion.governorateId);

  const onApprove = async (nameAr: string, nameEn: string) => {
    try {
      await approveMutation.mutateAsync({ id: suggestion.id, nameAr, nameEn });
      toast.show({
        message: t("areaSuggestions.approvedToast", { name: locale === "ar" ? nameAr : nameEn || nameAr }),
      });
    } catch (err) {
      if (err instanceof ApiJsonClientError) {
        if (err.status === 400) {
          throw new ApiJsonClientError({
            message: t(keys.areaSuggestions.alreadyReviewedError),
            status: err.status,
          });
        }
        if (err.status === 409) {
          throw new ApiJsonClientError({
            message: t(keys.areaSuggestions.duplicateNameError),
            status: err.status,
          });
        }
      }
      throw err;
    }
  };

  const onReject = async () => {
    setRejectError(null);
    try {
      await rejectMutation.mutateAsync(suggestion.id);
      toast.show({ message: t("areaSuggestions.rejectedToast", { name: suggestion.rawName }) });
      setIsRejectConfirmOpen(false);
    } catch (err) {
      setRejectError(
        err instanceof ApiJsonClientError && err.status === 400
          ? t(keys.areaSuggestions.alreadyReviewedError)
          : err instanceof ApiJsonClientError
            ? err.message || ""
            : "",
      );
    }
  };

  return (
    <>
      <span style={{ display: "inline-flex", gap: 2 }}>
        <Button
          variant="ghost"
          iconOnly
          icon={Check}
          title={`${t(keys.areaSuggestions.approveAction)}: ${suggestion.rawName}`}
          onClick={() => setIsApproveOpen(true)}
        />
        <Button
          variant="ghost"
          iconOnly
          icon={X}
          title={`${t(keys.areaSuggestions.rejectAction)}: ${suggestion.rawName}`}
          onClick={() => {
            setRejectError(null);
            setIsRejectConfirmOpen(true);
          }}
        />
      </span>

      {isApproveOpen ? (
        <AreaEditor
          onClose={() => setIsApproveOpen(false)}
          approveSuggestion={{
            governorateId: suggestion.governorateId,
            governorateName,
            initialNameAr: suggestion.rawName,
            onApprove,
          }}
        />
      ) : null}

      <Modal
        open={isRejectConfirmOpen}
        onClose={rejectMutation.isPending ? undefined : () => setIsRejectConfirmOpen(false)}
        title={t("areaSuggestions.rejectTitle")}
        width={400}
        actions={
          <>
            <Button
              variant="ghost"
              muted
              disabled={rejectMutation.isPending}
              onClick={() => setIsRejectConfirmOpen(false)}
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              loadingLabel={t("areaSuggestions.rejecting")}
              onClick={() => void onReject()}
            >
              {rejectError !== null ? t(keys.shell.retry) : t(keys.areaSuggestions.rejectAction)}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rejectError !== null ? (
            <Banner tone="error" title={t("areaSuggestions.rejectFailedTitle")}>
              {[rejectError, t("areaSuggestions.rejectFailedBody")].filter(Boolean).join(" ")}
            </Banner>
          ) : null}
          <p className="rd-modal-text" style={{ margin: 0 }}>
            {t(keys.areaSuggestions.rejectConfirm)}
          </p>
        </div>
      </Modal>
    </>
  );
}
